import { ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';
import { isLiveJwt } from '@/util/jwtUtil';
import { initiateOneSdk } from '@/integrations/FrankieOne/initiateOneSdk';
import { AmethystClient } from '@/integrations/Immersve/AmethystClient';
import { useImmersveAuthStore } from '@/stores/auth.store';
import type { KycSessionData } from '@/stores/auth.store';
import { KycSessionStorage } from '@/common/KycSessionStorage';
import { extractFrankieAuthSentryData } from '@/util/sentry/eventProcessing';
import * as Sentry from '@sentry/vue';
import type { KycSession } from '@/models/KycSession';

export interface FrankieOneSessionToken {
  data?: {
    entityId: string;
  };
  exp?: number;
}

export interface OneSdk {
  flow: (type: string) => {
    on: (event: string, callback: (payload) => void) => void;
    mount: (element: string) => void;
  }; //https://apidocs.frankiefinancial.com/docs/idv-flow
}

function getStoredTokenForSession(sessionId: string | undefined) {
  if (!sessionId) return undefined;
  const storage = KycSessionStorage.getDataForSession<KycSessionData>(sessionId);
  return storage?.externalSession?.token;
}

export const useFrankieOneStore = defineStore('frankie-one', () => {
  const authStore = useImmersveAuthStore();

  const rawToken = ref<string | undefined>(getStoredTokenForSession(authStore.sessionId));

  const isValidToken = computed(() => {
    if (!rawToken.value) {
      return false;
    }
    return isLiveJwt(rawToken.value);
  });

  const oneSdk = ref(undefined);

  const idv = computed(() => {
    if (!oneSdk.value) {
      return undefined;
    }
    const sdk: OneSdk = oneSdk.value;
    return sdk.flow('idv');
  });

  function saveSessionToken(newToken: string) {
    rawToken.value = newToken;

    if (!authStore.sessionId) {
      throw new Error('no session id set');
    }

    KycSessionStorage.updateDataForSession<KycSessionData>(authStore.sessionId, {
      externalSession: {
        token: newToken,
      },
    });
  }

  function clearSessionToken() {
    if (authStore.sessionId) {
      rawToken.value = undefined;

      KycSessionStorage.updateDataForSession<KycSessionData>(authStore.sessionId, {
        externalSession: {
          token: '',
        },
      });
    }
  }

  async function initialiseOneSdk({
    version,
    onfidoSdkVersion,
  }: {
    version: string;
    onfidoSdkVersion?: string;
  }) {
    if (!rawToken.value || !isValidToken.value) {
      return;
    }
    const sdk = await initiateOneSdk({
      token: rawToken.value,
      version,
      onfidoSdkVersion,
    });

    oneSdk.value = sdk;
  }

  async function loadOneSdk(kycSession: KycSession) {
    clearSessionToken();
    await requestAndLoadExternalToken();
    await initialiseOneSdk({
      version: kycSession.oneSdkVersion,
      onfidoSdkVersion: kycSession.onfidoSdkVersion,
    });
  }

  async function requestAndLoadExternalToken() {
    const amethystClient = new AmethystClient();
    if (authStore.sessionId) {
      const externalKycSession = await amethystClient.getExternalKycSessionToken(
        authStore.sessionId
      );
      saveSessionToken(externalKycSession.token);
    } else {
      throw new Error('Session ID is undefined');
    }
  }

  function trySetSentryTagsFromFrankieOneAccessToken(token: string | undefined) {
    if (!token) {
      return;
    }
    const { frankieOneEntityId } = extractFrankieAuthSentryData(token);
    if (frankieOneEntityId) {
      Sentry.setTag('frankieOneEntityId', frankieOneEntityId);
    }
  }

  watch(
    () => rawToken,
    (token) => {
      trySetSentryTagsFromFrankieOneAccessToken(token.value);
    }
  );

  return {
    idv,
    oneSdk,
    isValidToken,
    clearSessionToken,
    loadOneSdk,
  };
});
